import { Dialog, Flex, Text } from '@servicetitan/anvil2';
import { openWebUrl } from '@servicetitan/financing-utils-shared';
import {
    SetupDataStore,
    WorkflowStore,
    useTurnsIntegration,
} from '@servicetitan/financing-workflow-shared';
import { useState, useCallback } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
    ExternalApplicationResponse,
    LoanApplicationSubmitSource2,
    SubmittingUser,
} from '@servicetitan/financing-api-client';
import { PrequalChoiceScreen } from './PrequalChoiceScreen';
import { PrequalApprovedScreen } from './PrequalApprovedScreen';
import { PrequalDeclinedScreen } from './PrequalDeclinedScreen';
import { ExternalApplicationFormDialog } from '../ExternalApplicationFormDialog';
import { OpenApplicationConfirmationDialog } from '../OpenApplicationConfirmationDialog';

type FlowState =
    | 'choice'
    | 'prequal-form'
    | 'prequal-approved'
    | 'prequal-declined'
    | 'full-application'
    | 'confirmation';

export interface PrequalFlowRendererProps {
    onClose?: () => void;
    allowSendViaEmail: boolean;
    showTechnicianInstructions: boolean;
    submittingUser?: SubmittingUser;
    source: LoanApplicationSubmitSource2;
    approvedAmount: number;
    onOpenApplication?: (response?: ExternalApplicationResponse) => void;
}

export const PrequalFlowRenderer = ({
    onClose,
    allowSendViaEmail,
    showTechnicianInstructions,
    submittingUser,
    source,
    approvedAmount,
    onOpenApplication,
}: PrequalFlowRendererProps) => {
    const flags = useFlags();
    const [flowState, setFlowState] = useState<FlowState>('choice');
    const { setupData } = SetupDataStore.useStore();
    const { setWorkflowStage } = WorkflowStore.useStore();
    const turnsIntegration = useTurnsIntegration();

    const [prequalResponse, setPrequalResponse] = useState<ExternalApplicationResponse | null>(
        null,
    );

    const handlePrequalify = useCallback(() => {
        if (turnsIntegration.isEnabled) {
            setWorkflowStage('application-with-co-application');
            return;
        }
        setFlowState('prequal-form');
    }, [turnsIntegration.isEnabled, setWorkflowStage]);

    const handleApplyNow = useCallback(() => {
        setFlowState('full-application');
    }, []);

    const handleBackToChoice = useCallback(() => {
        setFlowState('choice');
        setPrequalResponse(null);
    }, []);

    const handlePrequalificationResponse = useCallback(
        (response?: ExternalApplicationResponse, isPrequalification = true) => {
            if (!response) {
                if (isPrequalification) {
                    setFlowState('prequal-declined');
                    return;
                }
                return;
            }

            setPrequalResponse(response);

            if (!isPrequalification) {
                return;
            }

            if (response.prequalificationOutcome === 'approved') {
                setFlowState('prequal-approved');
            } else if (response.prequalificationOutcome === 'declined') {
                setFlowState('prequal-declined');
            } else {
                // Fallback for legacy backend responses without prequalificationOutcome
                setFlowState(response.accessLink ? 'prequal-approved' : 'prequal-declined');
            }
        },
        [],
    );

    const handlePrequalApplyNow = useCallback(() => {
        if (prequalResponse?.accessLink) {
            openWebUrl(prequalResponse.accessLink, setupData?.useInAppBrowser);
        }
        setFlowState('confirmation');

        if (prequalResponse?.id) {
            onOpenApplication?.(prequalResponse);
        }
    }, [prequalResponse, onOpenApplication, setupData?.useInAppBrowser]);

    switch (flowState) {
        case 'choice':
            if (!flags.wellsfargoPrequals && setupData?.provider?.toLowerCase() === 'wellsfargo') {
                setFlowState('full-application');
                return;
            }
            return (
                <>
                    <Dialog.Header>
                        <Flex justifyContent="space-between" alignItems="center">
                            <Text variant="headline" size="large" el="h2">
                                Select a way to apply
                            </Text>
                        </Flex>
                    </Dialog.Header>

                    <Dialog.Content>
                        <PrequalChoiceScreen
                            onPrequalify={handlePrequalify}
                            onApplyNow={handleApplyNow}
                        />
                    </Dialog.Content>
                </>
            );

        case 'prequal-form':
            return (
                <ExternalApplicationFormDialog
                    onSendViaEmail={() => {}}
                    onOpenApplication={handlePrequalificationResponse}
                    onCancel={handleBackToChoice}
                    allowSendViaEmail={allowSendViaEmail}
                    showTechnicianInstructions={showTechnicianInstructions}
                    submittingUser={submittingUser}
                    source={source}
                    isPrequalification
                    submitButtonText="See If You Pre-qualify"
                    showLaunchIcon={false}
                />
            );

        case 'prequal-approved':
            return (
                <>
                    <Dialog.Header />
                    <Dialog.Content>
                        <PrequalApprovedScreen
                            approvedAmount={approvedAmount}
                            onApplyNow={handlePrequalApplyNow}
                        />
                    </Dialog.Content>
                </>
            );

        case 'prequal-declined':
            return (
                <>
                    <Dialog.Header />
                    <Dialog.Content>
                        <PrequalDeclinedScreen onApplyNow={handleApplyNow} />
                    </Dialog.Content>
                </>
            );

        case 'full-application':
            return (
                <ExternalApplicationFormDialog
                    onSendViaEmail={() => {}}
                    onOpenApplication={handlePrequalificationResponse}
                    onCancel={handleBackToChoice}
                    allowSendViaEmail={allowSendViaEmail}
                    showTechnicianInstructions={showTechnicianInstructions}
                    submittingUser={submittingUser}
                    source={source}
                    isPrequalification={false}
                    submitButtonText="Apply Now"
                    showLaunchIcon
                />
            );
        case 'confirmation':
            return <OpenApplicationConfirmationDialog onReturnToProposals={() => onClose?.()} />;
        default:
            return null;
    }
};
